/**
 * Emerald Books — Pre-Sales module.
 *
 * Quotes (customer) + Purchase Orders (vendor).
 *   • List, create, edit (draft only), delete
 *   • Status transitions: draft → sent → accepted/declined/cancelled
 *   • Convert quote → invoice draft (or PO → bill draft)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Loader2, Plus, Trash2, Send, XCircle, ArrowRight, CheckCircle2, Pencil,
  FileSpreadsheet, Search, ChevronRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { booksClient, unwrapList, errMsg } from './api';

const money = (n) => (Number(n) || 0).toFixed(2);
const emptyLine = () => ({ description: '', quantity: 1, unit_price: 0, account_id: '' });

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-600',
  sent: 'bg-blue-100 text-blue-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  declined: 'bg-rose-100 text-rose-700',
  cancelled: 'bg-slate-100 text-slate-400',
  converted: 'bg-violet-100 text-violet-700',
};

/**
 * Shared page for Quotes (kind=quote) and Purchase Orders (kind=purchase_order).
 * kind → chooses contact type (customer/vendor) and account type (income/expense).
 */
const PreDocsPage = ({ kind, title, description, testIdPrefix }) => {
  const contactType = kind === 'quote' ? 'customer' : 'vendor';
  const accountType = kind === 'quote' ? 'income' : 'expense';
  const targetLabel = kind === 'quote' ? 'invoice' : 'bill';

  const [rows, setRows] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    contact_id: '',
    date: new Date().toISOString().slice(0, 10),
    valid_until: '',
    reference: '',
    notes: '',
    lines: [emptyLine()],
  });

  const load = async () => {
    setLoading(true);
    try {
      const [r, c, a] = await Promise.all([
        booksClient.get('/pre-docs', { params: { kind, ...(filterStatus ? { status: filterStatus } : {}) } }),
        booksClient.get('/contacts'),
        booksClient.get('/ledger/accounts'),
      ]);
      setRows(r.data);
      setContacts(unwrapList(c));
      setAccounts(unwrapList(a));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [kind, filterStatus]);

  const filteredContacts = useMemo(() => contacts.filter((c) => c.type === contactType), [contacts, contactType]);
  const filteredAccounts = useMemo(() => accounts.filter((a) => a.type === accountType), [accounts, accountType]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => {
      const contact = contacts.find((c) => c.contact_id === r.contact_id);
      return [r.doc_no, r.reference, contact?.name].filter(Boolean).some((v) => String(v).toLowerCase().includes(s));
    });
  }, [rows, q, contacts]);

  const totals = (lines) => lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);

  const updateLine = (i, patch) => setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, ...patch } : l) }));
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  const openCreate = () => {
    setEditing(null);
    setForm({
      contact_id: '', date: new Date().toISOString().slice(0, 10),
      valid_until: '', reference: '', notes: '', lines: [emptyLine()],
    });
    setShowForm(true);
  };

  const openEdit = (r) => {
    if (r.status !== 'draft') { toast.error('Only draft documents can be edited'); return; }
    setEditing(r);
    setForm({
      contact_id: r.contact_id,
      date: r.date,
      valid_until: r.valid_until || '',
      reference: r.reference || '',
      notes: r.notes || '',
      lines: r.lines?.length ? r.lines : [emptyLine()],
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const lines = form.lines.filter((l) => l.description && l.account_id);
    if (!lines.length) { toast.error('Add at least one line with description + account'); return; }
    try {
      if (editing) {
        await booksClient.patch(`/pre-docs/${editing.doc_id}`, {
          date: form.date, valid_until: form.valid_until || null,
          reference: form.reference, notes: form.notes, lines,
        });
        toast.success('Updated');
      } else {
        await booksClient.post('/pre-docs', {
          kind, contact_id: form.contact_id,
          date: form.date, valid_until: form.valid_until || null,
          reference: form.reference, notes: form.notes, lines,
        });
        toast.success('Created');
      }
      setShowForm(false); setEditing(null); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const setStatus = async (r, s) => {
    try { await booksClient.post(`/pre-docs/${r.doc_id}/status/${s}`); toast.success(`Marked ${s}`); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const convert = async (r) => {
    if (!confirm(`Convert ${r.doc_no} to a draft ${targetLabel}?`)) return;
    try {
      const { data } = await booksClient.post(`/pre-docs/${r.doc_id}/convert`);
      toast.success(`Converted → ${data.doc_no}`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const del = async (r) => {
    if (!confirm(`Delete ${r.doc_no}?`)) return;
    try { await booksClient.delete(`/pre-docs/${r.doc_id}`); toast.success('Deleted'); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="mx-auto max-w-6xl" data-testid={`books-${testIdPrefix}-page`}>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">{title}</h1>
          <p className="mt-1 text-sm text-slate-500">{description}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-slate-400" />
            <Input placeholder="Search…" className="pl-7" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testIdPrefix}-search`} />
          </div>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="rounded-md border px-2 py-1.5 text-sm" data-testid={`${testIdPrefix}-filter`}>
            <option value="">All statuses</option>
            {Object.keys(STATUS_COLORS).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <Button onClick={openCreate} className="bg-emerald-600 text-white hover:bg-emerald-500" data-testid={`${testIdPrefix}-new-btn`}>
            <Plus className="mr-1 h-4 w-4" /> New
          </Button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={submit} className="mb-6 rounded-xl border bg-white p-5 shadow-sm" data-testid={`${testIdPrefix}-form`}>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <label className="text-sm md:col-span-2">
              <div className="mb-1 text-xs text-slate-500">{contactType === 'customer' ? 'Customer' : 'Vendor'}</div>
              <select required disabled={!!editing} value={form.contact_id} onChange={(e) => setForm({ ...form, contact_id: e.target.value })} className="w-full rounded-md border px-2 py-1.5 text-sm" data-testid={`${testIdPrefix}-contact`}>
                <option value="">Select…</option>
                {filteredContacts.map((c) => <option key={c.contact_id} value={c.contact_id}>{c.name}</option>)}
              </select>
            </label>
            <label className="text-sm">
              <div className="mb-1 text-xs text-slate-500">Date</div>
              <Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </label>
            <label className="text-sm">
              <div className="mb-1 text-xs text-slate-500">Valid until (optional)</div>
              <Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
            </label>
            <label className="text-sm md:col-span-4">
              <div className="mb-1 text-xs text-slate-500">Reference</div>
              <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
            </label>
          </div>

          <div className="mt-4">
            <div className="mb-1 text-xs font-medium text-slate-600">Lines</div>
            {form.lines.map((l, i) => (
              <div key={i} className="mb-2 grid grid-cols-12 gap-2">
                <Input className="col-span-4" placeholder="Description" value={l.description} onChange={(e) => updateLine(i, { description: e.target.value })} data-testid={`${testIdPrefix}-line-desc-${i}`} />
                <Input className="col-span-1" type="number" step="0.01" value={l.quantity} onChange={(e) => updateLine(i, { quantity: parseFloat(e.target.value) || 0 })} data-testid={`${testIdPrefix}-line-qty-${i}`} />
                <Input className="col-span-2" type="number" step="0.01" placeholder="Unit price" value={l.unit_price} onChange={(e) => updateLine(i, { unit_price: parseFloat(e.target.value) || 0 })} data-testid={`${testIdPrefix}-line-price-${i}`} />
                <select className="col-span-4 rounded-md border px-2 text-sm" value={l.account_id} onChange={(e) => updateLine(i, { account_id: e.target.value })} data-testid={`${testIdPrefix}-line-account-${i}`}>
                  <option value="">Account…</option>
                  {filteredAccounts.map((a) => <option key={a.account_id} value={a.account_id}>{a.code} · {a.name}</option>)}
                </select>
                <button type="button" onClick={() => removeLine(i)} className="col-span-1 text-rose-600 hover:text-rose-700" disabled={form.lines.length === 1}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <button type="button" onClick={addLine} className="text-sm text-emerald-600 hover:underline" data-testid={`${testIdPrefix}-add-line`}>+ Add line</button>
          </div>

          <div className="mt-4 flex items-center justify-between border-t pt-3">
            <div className="text-sm text-slate-500">Total: <span className="font-semibold text-slate-900">{money(totals(form.lines))}</span></div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-600 text-white hover:bg-emerald-500" data-testid={`${testIdPrefix}-save`}>{editing ? 'Update' : 'Create'}</Button>
            </div>
          </div>
        </form>
      )}

      <div className="rounded-xl border bg-white">
        {loading ? <div className="p-6 text-slate-400"><Loader2 className="inline h-4 w-4 animate-spin" /> Loading…</div> :
          filtered.length === 0 ? <div className="p-10 text-center text-slate-400">No {title.toLowerCase()} yet.</div> :
          <table className="w-full text-sm">
            <thead className="border-b bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="p-3 text-left">No.</th>
                <th className="p-3 text-left">{contactType === 'customer' ? 'Customer' : 'Vendor'}</th>
                <th className="p-3 text-left">Date</th>
                <th className="p-3 text-right">Total</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const contact = contacts.find((c) => c.contact_id === r.contact_id);
                return (
                  <tr key={r.doc_id} className="border-b last:border-b-0" data-testid={`${testIdPrefix}-row-${r.doc_no}`}>
                    <td className="p-3 font-mono text-xs">{r.doc_no}</td>
                    <td className="p-3">{contact?.name || <span className="text-slate-400">—</span>}</td>
                    <td className="p-3">{r.date}</td>
                    <td className="p-3 text-right font-medium">{money(r.total)}</td>
                    <td className="p-3">
                      <span className={'rounded-full px-2 py-0.5 text-xs ' + (STATUS_COLORS[r.status] || 'bg-slate-100 text-slate-500')}>{r.status}</span>
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        {r.status === 'draft' && <>
                          <button onClick={() => setStatus(r, 'sent')} className="rounded p-1 text-slate-500 hover:text-blue-600" title="Mark sent" data-testid={`${testIdPrefix}-send-${r.doc_no}`}><Send className="h-3.5 w-3.5" /></button>
                          <button onClick={() => openEdit(r)} className="rounded p-1 text-slate-500 hover:text-emerald-600" title="Edit" data-testid={`${testIdPrefix}-edit-${r.doc_no}`}><Pencil className="h-3.5 w-3.5" /></button>
                        </>}
                        {r.status === 'sent' && <>
                          <button onClick={() => setStatus(r, 'accepted')} className="rounded p-1 text-slate-500 hover:text-emerald-600" title="Accept" data-testid={`${testIdPrefix}-accept-${r.doc_no}`}><CheckCircle2 className="h-3.5 w-3.5" /></button>
                          <button onClick={() => setStatus(r, 'declined')} className="rounded p-1 text-slate-500 hover:text-rose-600" title="Decline"><XCircle className="h-3.5 w-3.5" /></button>
                        </>}
                        {['sent', 'accepted'].includes(r.status) && !r.converted_to_id && (
                          <button onClick={() => convert(r)} className="rounded p-1 text-slate-500 hover:text-violet-600" title={`Convert to ${targetLabel}`} data-testid={`${testIdPrefix}-convert-${r.doc_no}`}><ArrowRight className="h-3.5 w-3.5" /></button>
                        )}
                        {['draft', 'cancelled'].includes(r.status) && (
                          <button onClick={() => del(r)} className="rounded p-1 text-slate-500 hover:text-rose-600" title="Delete" data-testid={`${testIdPrefix}-delete-${r.doc_no}`}><Trash2 className="h-3.5 w-3.5" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        }
      </div>
    </div>
  );
};

export const QuotesPage = () => (
  <PreDocsPage
    kind="quote"
    title="Quotes"
    description="Send price offers to customers. Accepted quotes convert to draft invoices."
    testIdPrefix="quotes"
  />
);

export const PurchaseOrdersPage = () => (
  <PreDocsPage
    kind="purchase_order"
    title="Purchase Orders"
    description="Order goods/services from vendors. Received POs convert to draft bills."
    testIdPrefix="purchase-orders"
  />
);
